<template>
  <div class="dashboard-container p-4 bg-white rounded-lg shadow-sm mb-6">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-bold">Locations Overview</h2>
      <div class="flex items-center gap-2">
        <v-btn
          color="primary"
          icon
          @click="captureScreenshot"
          title="Save dashboard as image"
        >
          <v-icon>mdi-download-box-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      v-if="!locations.length"
      class="bg-amber-50 p-6 rounded-lg border border-amber-200 flex flex-col items-center justify-center"
    >
      <v-icon color="amber darken-2" size="36" class="mb-3"
        >mdi-alert-circle-outline</v-icon
      >
      <p class="text-amber-700 font-medium text-center">
        No locations available
      </p>
      <p class="text-amber-600 text-sm text-center mt-2">
        Connect Google Business Locations to see insights
      </p>
    </div>

    <div v-else>
      <!-- Locations Summary Stats -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Total Locations</h3>
            <v-icon color="primary">mdi-map-marker-multiple</v-icon>
          </div>
          <div class="text-2xl font-bold text-primary mt-2">
            {{ locations.length }}
          </div>
          <div class="mt-1 text-xs text-gray-500">Connected Locations</div>
        </div>

        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Avg Rating</h3>
            <v-icon color="amber darken-1">mdi-star</v-icon>
          </div>
          <div class="text-2xl font-bold text-amber-600 mt-2">
            {{ averageRating }}
          </div>
          <div class="mt-1 text-xs text-gray-500">
            Average across all locations
          </div>
        </div>

        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Profile Health</h3>
            <v-icon color="success">mdi-check-circle</v-icon>
          </div>
          <div class="text-2xl font-bold text-green-600 mt-2">
            {{ averageHealth }}%
          </div>
          <div class="mt-1 text-xs text-gray-500">
            Average profile completeness
          </div>
        </div>

        <div class="stat-card bg-white border border-slate-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <h3 class="text-gray-600 font-semibold">Total Reviews</h3>
            <v-icon color="primary">mdi-comment-text-multiple</v-icon>
          </div>
          <div class="text-2xl font-bold text-primary mt-2">
            {{ totalReviews }}
          </div>
          <div class="mt-1 text-xs text-gray-500">Across all locations</div>
        </div>
      </div>

      <!-- Connection Status -->
      <div class="mb-6">
        <h3 class="font-semibold mb-3 text-slate-700">Connection Status</h3>
        <div
          class="grid gap-3"
          :class="{
            'grid-cols-2 sm:grid-cols-4': !shallowConnectedLocations,
            'grid-cols-2 sm:grid-cols-5': shallowConnectedLocations > 0,
          }"
        >
          <div
            class="status-item p-3 rounded-lg bg-green-50 border border-green-100"
            @click="connectedLocations > 0 && filterByStatus('connected')"
            :class="{
              'cursor-pointer hover:transform hover:-translate-y-1':
                connectedLocations > 0,
              'opacity-60': connectedLocations === 0,
            }"
          >
            <div class="text-xl font-bold text-green-600">
              {{ connectedLocations }}
            </div>
            <div class="text-sm text-green-700">Connected</div>
          </div>
          <div
            v-if="shallowConnectedLocations > 0"
            class="status-item p-3 rounded-lg bg-indigo-50 border border-indigo-100"
            @click="filterByStatus('shallow')"
            :class="{
              'cursor-pointer hover:transform hover:-translate-y-1':
                shallowConnectedLocations > 0,
              'opacity-60': shallowConnectedLocations === 0,
            }"
          >
            <div class="flex items-center justify-between">
              <div class="text-xl font-bold text-indigo-600">
                {{ shallowConnectedLocations }}
              </div>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="indigo" v-bind="attrs" v-on="on"
                    >mdi-information</v-icon
                  >
                </template>
                <span
                  >Fly can only read public profiles and reviews. Cannot post.
                  Cannot read stats, search insights.</span
                >
              </v-tooltip>
            </div>
            <div class="text-sm text-indigo-700">Shallow Connected</div>
          </div>
          <div
            class="status-item p-3 rounded-lg bg-amber-50 border border-amber-100"
            @click="verifiedLocations > 0 && filterByStatus('verified')"
            :class="{
              'cursor-pointer hover:transform hover:-translate-y-1':
                verifiedLocations > 0,
              'opacity-60': verifiedLocations === 0,
            }"
          >
            <div class="text-xl font-bold text-amber-600">
              {{ verifiedLocations }}
            </div>
            <div class="text-sm text-amber-700">Verified</div>
          </div>
          <div
            class="status-item p-3 rounded-lg bg-blue-50 border border-blue-100"
            @click="unverifiedLocations > 0 && filterByStatus('unverified')"
            :class="{
              'cursor-pointer hover:transform hover:-translate-y-1':
                unverifiedLocations > 0,
              'opacity-60': unverifiedLocations === 0,
            }"
          >
            <div class="text-xl font-bold text-blue-600">
              {{ unverifiedLocations }}
            </div>
            <div class="text-sm text-blue-700">Unverified</div>
          </div>
          <div
            class="status-item p-3 rounded-lg bg-red-50 border border-red-100"
            @click="
              authRequiredLocations > 0 && filterByStatus('auth_required')
            "
            :class="{
              'cursor-pointer hover:transform hover:-translate-y-1':
                authRequiredLocations > 0,
              'opacity-60': authRequiredLocations === 0,
            }"
          >
            <div class="text-xl font-bold text-red-600">
              {{ authRequiredLocations }}
            </div>
            <div class="text-sm text-red-700">Auth Required</div>
          </div>
        </div>
      </div>

      <!-- Distributions - Combined Row -->
      <div class="mb-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Rating Distribution Chart -->
          <div>
            <h3 class="font-semibold mb-3 text-slate-700">
              Rating Distribution
            </h3>
            <div class="flex flex-col gap-2">
              <div
                v-for="rating in [5, 4, 3, 2, 1]"
                :key="rating"
                class="flex items-center"
              >
                <div class="w-8 text-right mr-2 flex-none">{{ rating }} ★</div>
                <div
                  class="flex-grow bg-slate-300 rounded-md h-5 overflow-hidden relative"
                >
                  <div
                    :class="`bg-${getRatingColor(rating)}`"
                    :style="`width: ${
                      ((ratingDistribution[rating] || 0) / locations.length) *
                      100
                    }%`"

                    class="h-full flex items-center z-40"
                  ></div>
                  <span
                    class="absolute top-1/2 transform -translate-y-1/2 left-2 text-xs text-white font-medium z-50 -my-1"
                  >
                    {{ ratingLabel(rating) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Reviews Distribution Chart -->
          <div>
            <h3 class="font-semibold mb-3 text-slate-700">
              Reviews Distribution
            </h3>
            <div class="flex flex-col gap-2">
              <div
                v-for="(segment, index) in reviewsDistribution"
                :key="index"
                class="flex items-center"
              >
                <div class="w-14 text-right mr-2 flex-none text-xs">
                  {{ segment.label }}
                </div>
<div
                  class="flex-grow bg-slate-300 rounded-md h-5 overflow-hidden relative"
                >
                  <div
                    :class="`bg-${segment.color}`"
                    :style="`width: ${Math.max(
                      1,
                      (segment.count / locations.length) * 100
                    )}%`"
                    class="h-full flex items-center left-0 z-40"
                  ></div>
                  <span
                    :class="`${segment.textColor}`"
                    class="absolute top-1/2 transform -translate-y-1/2 left-2 text-xs font-medium z-50 -my-1"
                  >
                    {{ segmentLabel(segment) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Locations Chart -->
      <div>
        <h3 class="font-semibold mb-3 text-slate-700">
          Top 5 Locations by Reviews
        </h3>
        <div v-if="topLocations.length" class="grid grid-cols-1 gap-3">
          <div
            v-for="(location, index) in topLocations"
            :key="index"
            class="top-location p-3 border border-slate-200 rounded-lg flex items-center"
          >
            <!-- Fixed width container for rank to ensure alignment -->
            <div class="flex-none w-12 flex justify-center">
              <div
                class="rank w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary font-bold leading-none"
              >
                {{ index + 1 }}
              </div>
            </div>
            <!-- Location details with modified width and removed truncate -->
            <div class="flex-grow min-w-0 mr-4">
              <div class="font-medium text-slate-800 break-words">
                {{ location.name || location.storeLocation }}
              </div>
              <div class="text-sm text-slate-600">
                {{ location.city }}, {{ location.state }}
              </div>
            </div>
            <!-- Fixed width for rating/reviews -->
            <div class="flex-none w-28 flex flex-col items-end">
              <div class="flex items-center text-amber-600">
                <v-icon small color="amber darken-1">mdi-star</v-icon>
                <span class="ml-1 font-medium">{{
                  location.averageRating || 0
                }}</span>
              </div>
              <div class="text-sm text-slate-600">
                {{ location.reviewCount || 0 }} reviews
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4 text-gray-500">
          No review data available
        </div>
      </div>

      <!-- Additional Insight Tables -->
      <div class="px-6 pt-6 pb-3">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">
          Location Insights
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Bottom 5 Locations by Reviews -->
          <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h4 class="font-medium text-gray-700 mb-3 flex items-center">
              <v-icon small color="error" class="mr-2"
                >mdi-chart-line-variant</v-icon
              >
              Bottom 5 Locations by Reviews
            </h4>
            <div class="overflow-auto max-h-80">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="py-2 px-3 text-left font-medium text-gray-500">
                      Location
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Reviews
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(location, idx) in bottomLocationsByReviews"
                    :key="idx"
                    class="border-t border-gray-100"
                  >
                    <td
                      class="py-2 px-3 text-gray-800 font-medium truncate max-w-[180px]"
                    >
                      {{ location.name || location.storeLocation }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      {{ location.reviewCount || 0 }}
                    </td>
                  </tr>
                  <tr
                    v-if="bottomLocationsByReviews.length === 0"
                    class="border-t border-gray-100"
                  >
                    <td colspan="2" class="py-3 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Top 5 Locations by Rating -->
          <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h4 class="font-medium text-gray-700 mb-3 flex items-center">
              <v-icon small color="success" class="mr-2">mdi-star</v-icon>
              Top 5 Locations by Rating
            </h4>
            <div class="overflow-auto max-h-80">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="py-2 px-3 text-left font-medium text-gray-500">
                      Location
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Rating
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(location, idx) in topLocationsByRating"
                    :key="idx"
                    class="border-t border-gray-100"
                  >
                    <td
                      class="py-2 px-3 text-gray-800 font-medium truncate max-w-[180px]"
                    >
                      {{ location.name || location.storeLocation }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      <div class="flex items-center justify-end">
                        <span class="mr-1">{{
                          (location.averageRating || 0).toFixed(1)
                        }}</span>
                        <v-icon x-small color="amber darken-2">mdi-star</v-icon>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="topLocationsByRating.length === 0"
                    class="border-t border-gray-100"
                  >
                    <td colspan="2" class="py-3 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Bottom 5 Locations by Rating -->
          <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h4 class="font-medium text-gray-700 mb-3 flex items-center">
              <v-icon small color="warning" class="mr-2"
                >mdi-star-outline</v-icon
              >
              Bottom 5 Locations by Rating
            </h4>
            <div class="overflow-auto max-h-80">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="py-2 px-3 text-left font-medium text-gray-500">
                      Location
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Rating
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(location, idx) in bottomLocationsByRating"
                    :key="idx"
                    class="border-t border-gray-100"
                  >
                    <td
                      class="py-2 px-3 text-gray-800 font-medium truncate max-w-[180px]"
                    >
                      {{ location.name || location.storeLocation }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      <div class="flex items-center justify-end">
                        <span class="mr-1">{{
                          (location.averageRating || 0).toFixed(1)
                        }}</span>
                        <v-icon x-small color="amber darken-2">mdi-star</v-icon>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="bottomLocationsByRating.length === 0"
                    class="border-t border-gray-100"
                  >
                    <td colspan="2" class="py-3 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Location Distribution Section -->
        <h3 class="text-lg font-semibold text-gray-800 mt-8 mb-4">
          Location Distribution
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Locations by City -->
          <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h4 class="font-medium text-gray-700 mb-3 flex items-center">
              <v-icon small color="primary" class="mr-2">mdi-city</v-icon>
              Locations by City
            </h4>
            <div class="overflow-auto max-h-80">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="py-2 px-3 text-left font-medium text-gray-500">
                      City
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Count
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in locationsByCity"
                    :key="idx"
                    class="border-t border-gray-100"
                  >
                    <td class="py-2 px-3 text-gray-800 font-medium">
                      {{ item.name || "Unknown" }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      {{ item.count }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      {{ item.percentage }}%
                    </td>
                  </tr>
                  <tr
                    v-if="locationsByCity.length === 0"
                    class="border-t border-gray-100"
                  >
                    <td colspan="3" class="py-3 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Locations by State/County -->
          <div class="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h4 class="font-medium text-gray-700 mb-3 flex items-center">
              <v-icon small color="primary" class="mr-2">mdi-map</v-icon>
              Locations by State/Region
            </h4>
            <div class="overflow-auto max-h-80">
              <table class="min-w-full text-sm">
                <thead>
                  <tr class="bg-gray-50">
                    <th class="py-2 px-3 text-left font-medium text-gray-500">
                      State/Region
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Count
                    </th>
                    <th class="py-2 px-3 text-right font-medium text-gray-500">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, idx) in locationsByState"
                    :key="idx"
                    class="border-t border-gray-100"
                  >
                    <td class="py-2 px-3 text-gray-800 font-medium">
                      {{ item.name || "Unknown" }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      {{ item.count }}
                    </td>
                    <td class="py-2 px-3 text-right text-gray-600">
                      {{ item.percentage }}%
                    </td>
                  </tr>
                  <tr
                    v-if="locationsByState.length === 0"
                    class="border-t border-gray-100"
                  >
                    <td colspan="3" class="py-3 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";

export default {
  name: "LocationsSummaryDashboard",
  props: {
    locations: {
      type: Array,
      required: true,
    },
  },
  computed: {
    averageRating() {
      if (!this.locations.length) return "0.0";

      const sum = this.locations.reduce((total, location) => {
        return total + (location.averageRating || 0);
      }, 0);

      return (sum / this.locations.length).toFixed(1);
    },

    averageHealth() {
      if (!this.locations.length) return 0;

      const sum = this.locations.reduce((total, location) => {
        return total + (location.profile_strength || 0);
      }, 0);

      return Math.round(sum / this.locations.length);
    },

    totalReviews() {
      return this.locations.reduce((total, location) => {
        return total + (location.reviewCount || 0);
      }, 0);
    },

    ratingDistribution() {
      const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

      this.locations.forEach((location) => {
        const rating = Math.round(location.averageRating || 0);
        if (rating > 0 && rating <= 5) {
          distribution[rating]++;
        }
      });

      return distribution;
    },

    topLocations() {
      return [...this.locations]
        .sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0))
        .slice(0, 5);
    },

    connectedLocations() {
      return this.locations.filter((loc) => loc.status === "connected").length;
    },

    shallowConnectedLocations() {
      return this.locations.filter((loc) => loc.shallowConnect === true).length;
    },
    verifiedLocations() {
      return this.locations.filter(
        (loc) => loc.gmbProfile?.verificationState === "COMPLETED"
      ).length;
    },

    unverifiedLocations() {
      return this.locations.filter(
        (loc) =>
          loc.gmbProfile?.verificationState === "FAILED" ||
          loc.gmbProfile?.verificationState === "UNVERIFIED"
      ).length;
    },

    authRequiredLocations() {
      return this.locations.filter(
        (loc) => loc.oauth_status === "invalid_grant"
      ).length;
    },

    locationsWithDates() {
      return this.locations
        .filter((location) => {
          // Check both possible formats of opening date (shallow and deep connected)
          const hasShallowDate =
            location.gmb_shallow_profile_data?.opening_date;
          const hasDeepDate = location.gmbProfile?.openInfo?.openingDate;
          return hasShallowDate || hasDeepDate;
        })
        .map((location) => {
          // Get the date in standard format
          let openingDate;
          if (location.gmb_shallow_profile_data?.opening_date) {
            openingDate = new Date(
              location.gmb_shallow_profile_data.opening_date
            );
          } else if (location.gmbProfile?.openInfo?.openingDate) {
            // Convert Firestore timestamp if necessary
            if (location.gmbProfile.openInfo.openingDate.seconds) {
              openingDate = new Date(
                location.gmbProfile.openInfo.openingDate.seconds * 1000
              );
            } else {
              openingDate = new Date(location.gmbProfile.openInfo.openingDate);
            }
          }

          return {
            id: location.id || location.wid,
            name:
              location.name ||
              location.gmbProfile?.title ||
              location.storeLocation,
            storeLocation: location.storeLocation,
            openingDate: openingDate,
          };
        })
        .sort((a, b) => a.openingDate - b.openingDate); // Sort by opening date
    },

    timelineYears() {
      if (!this.locationsWithDates.length) return [new Date().getFullYear()];

      // Get earliest and latest years
      const years = this.locationsWithDates.map((loc) =>
        loc.openingDate.getFullYear()
      );
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);

      // Create array of years for timeline
      const result = [];
      for (let year = minYear; year <= maxYear; year++) {
        result.push(year);
      }

      return result.length ? result : [new Date().getFullYear()];
    },

    reviewsDistribution() {
      const distribution = [
        {
          label: "0 reviews",
          count: 0,
          color: "red-600",
          textColor: "text-white",
        },
        {
          label: "1-10",
          count: 0,
          color: "orange-500",
          textColor: "text-white",
        },
        {
          label: "11-50",
          count: 0,
          color: "teal-500",
          textColor: "text-white",
        },
        {
          label: "51-100",
          count: 0,
          color: "blue-600",
          textColor: "text-white",
        },
        {
          label: "101-500",
          count: 0,
          color: "purple-500",
          textColor: "text-white",
        },
        {
          label: "500+",
          count: 0,
          color: "green-600",
          textColor: "text-white",
        },
      ];

      this.locations.forEach((location) => {
        const reviewCount = location.reviewCount || 0;
        if (reviewCount === 0) {
          distribution[0].count++;
        } else if (reviewCount >= 1 && reviewCount <= 10) {
          distribution[1].count++;
        } else if (reviewCount > 10 && reviewCount <= 50) {
          distribution[2].count++;
        } else if (reviewCount > 50 && reviewCount <= 100) {
          distribution[3].count++;
        } else if (reviewCount > 100 && reviewCount <= 500) {
          distribution[4].count++;
        } else if (reviewCount > 500) {
          distribution[5].count++;
        }
      });

      return distribution;
    },

    topLocationsByRating() {
      return [...this.locations]
        .filter((location) => location.averageRating > 0)
        .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
        .slice(0, 5);
    },

    bottomLocationsByRating() {
      return [...this.locations]
        .filter((location) => location.averageRating > 0)
        .sort((a, b) => (a.averageRating || 0) - (b.averageRating || 0))
        .slice(0, 5);
    },

    bottomLocationsByReviews() {
      return [...this.locations]
        .filter((location) => (location.reviewCount || 0) > 0)
        .sort((a, b) => (a.reviewCount || 0) - (b.reviewCount || 0))
        .slice(0, 5);
    },

    locationsByCity() {
      const cityMap = new Map();
      let totalWithCity = 0;

      this.locations.forEach((location) => {
        if (location.city) {
          const city = location.city.trim();
          cityMap.set(city, (cityMap.get(city) || 0) + 1);
          totalWithCity++;
        }
      });

      return Array.from(cityMap.entries())
        .map(([name, count]) => ({
          name,
          count,
          percentage: totalWithCity
            ? Math.round((count / totalWithCity) * 100)
            : 0,
        }))
        .sort((a, b) => b.count - a.count);
    },

    locationsByState() {
      const stateMap = new Map();
      let totalWithState = 0;

      this.locations.forEach((location) => {
        if (location.state) {
          const state = location.state.trim();
          stateMap.set(state, (stateMap.get(state) || 0) + 1);
          totalWithState++;
        }
      });

      return Array.from(stateMap.entries())
        .map(([name, count]) => ({
          name,
          count,
          percentage: totalWithState
            ? Math.round((count / totalWithState) * 100)
            : 0,
        }))
        .sort((a, b) => b.count - a.count);
    },
  },
  methods: {
    segmentLabel(segment) {
      const pct = (segment.count / this.locations.length) * 100;
      const label = segment.count === 1 ? "location" : "locations";

      // Show the percentage only when the bar is at least 20 % wide.
      if (!segment?.count) {
        return "";
      }
      if (pct >= 20) {
        return `${segment.count} ${label} (${Math.round(pct)}%)`;
      }
      return `${segment.count} ${label}`;
    },
    ratingLabel(rating) {
      const count = this.ratingDistribution[rating] || 0;
      if (!count) {
        return "";
      }
      const pct = (count / this.locations.length) * 100;
      const base = `${count} ${count === 1 ? "location" : "locations"}`;
      return pct >= 20 ? `${base} (${Math.round(pct)}%)` : base;
    },
    getRatingColor(rating) {
      const colors = {
        1: "red-500",
        2: "orange-500",
        3: "amber-500",
        4: "lime-500",
        5: "green-500",
      };

      return colors[rating] || "gray-500";
    },

    filterByStatus(status) {
      this.$emit("filter-status", status);
    },

    formatOpeningDate(date) {
      if (!date) return "Unknown";
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    getTimelinePosition(date) {
      if (!date || !this.timelineYears.length) return 0;

      const minYear = this.timelineYears[0];
      const maxYear = this.timelineYears[this.timelineYears.length - 1];
      const totalDays = (maxYear - minYear + 1) * 365;

      const startDate = new Date(minYear, 0, 1);
      const diffDays = Math.round((date - startDate) / (1000 * 60 * 60 * 24));

      return (diffDays / totalDays) * 100;
    },

    isRecentOpening(date) {
      if (!date) return false;
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      return date >= threeMonthsAgo;
    },

    async captureScreenshot() {
      try {
        this.$store.commit("showSnackbar", {
          text: "Preparing screenshot...",
          color: "info",
        });

        const dashboardElement = document.querySelector(".dashboard-container");
        if (!dashboardElement) {
          throw new Error("Dashboard element not found");
        }

        const canvas = await html2canvas(dashboardElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        const imageData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imageData;

        const date = new Date();
        const formattedDate = date.toISOString().split("T")[0];
        link.download = `locations-dashboard-${formattedDate}.png`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$snackbar.show("Screenshot saved");
      } catch (error) {
        console.error("Error capturing screenshot:", error);
        this.$snackbar.show("Failed to save screenshot");
      }
    },
  },
};
</script>

<style scoped>
.status-item {
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.status-item.cursor-pointer:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.top-location {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.top-location:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.timeline-marker {
  transform: translateX(-50%);
}

.pulse-animation {
  box-shadow: 0 0 0 rgba(128, 80, 222, 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(128, 80, 222, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(128, 80, 222, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(128, 80, 222, 0);
  }
}
</style>
